import { CopyOutlined, EyeOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { App, Button, Modal } from "antd";
import { useState } from "react";

import { ApiError } from "../../api/client";
import {
  fetchProjectCloudDriveInfo,
  recordProjectCloudDriveInfoCopy,
  type ProjectCloudDriveInfoResponse,
} from "../../api/projects";

type CloudDriveInfoViewerProps =
  | {
      projectId: string;
      loadInfo?: never;
      recordCopy?: never;
    }
  | {
      projectId?: never;
      loadInfo: () => Promise<ProjectCloudDriveInfoResponse>;
      recordCopy: () => Promise<void>;
    };

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : fallback;
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("浏览器未允许复制");
}

export function CloudDriveInfoViewer(props: CloudDriveInfoViewerProps) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [cloudDriveInfo, setCloudDriveInfo] = useState<string | null>(null);
  const loadInfo = props.loadInfo ?? (() => fetchProjectCloudDriveInfo(props.projectId));
  const recordCopy = props.recordCopy ?? (() => recordProjectCloudDriveInfoCopy(props.projectId));

  const viewMutation = useMutation({
    mutationFn: loadInfo,
    onSuccess: (result) => {
      setCloudDriveInfo(result.cloud_drive_info);
      setOpen(true);
    },
    onError: (error) => message.error(errorMessage(error, "网盘信息读取失败")),
  });
  const copyMutation = useMutation({
    mutationFn: async () => {
      if (!cloudDriveInfo) return;
      await recordCopy();
      await copyText(cloudDriveInfo);
    },
    onSuccess: () => message.success("网盘信息已复制"),
    onError: (error) => message.error(errorMessage(error, "网盘信息复制失败")),
  });

  return (
    <>
      <Button
        size="small"
        icon={<EyeOutlined />}
        loading={viewMutation.isPending}
        onClick={() => viewMutation.mutate()}
      >
        查看
      </Button>
      <Modal
        title="网盘信息"
        open={open}
        width={560}
        destroyOnHidden
        footer={
          <>
            <Button onClick={() => setOpen(false)}>关闭</Button>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              disabled={!cloudDriveInfo}
              loading={copyMutation.isPending}
              onClick={() => copyMutation.mutate()}
            >
              一键复制
            </Button>
          </>
        }
        onCancel={() => setOpen(false)}
        afterClose={() => setCloudDriveInfo(null)}
      >
        <div className="project-cloud-drive-content">{cloudDriveInfo || "暂无网盘信息"}</div>
      </Modal>
    </>
  );
}
